import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Header,
  Grid,
  Message,
  List,
  Icon,
  Segment,
  Form,
  Input,
} from "semantic-ui-react";
import { PaymentDetails } from "./PaymentDetails";
import { OverrideNotification } from "./OverrideNotification";
import { FileUploadSection } from "./FileUploadSection";
import { fineApi } from "@api/fine/fine";
import { loanApi } from "@api/loans";
import { AuditService } from "@api/audit/audit";
import { userApi } from "@api/users/user";

// Constants
const RETURNABLE_STATES = ["ITEM_ON_LOAN", "ITEM_OVERDUE"];
const ACTIVE_FINE_STATUSES = ["active", "unpaid", "outstanding"];
const VERIFICATION_ATTEMPTS = 3;
const AUTO_CLOSE_DELAY = 3000;

const INITIAL_STATE = {
  patronFines: [],
  selectedFines: [],
  loading: false,
  error: "",
  paymentProcessing: false,
  usingFallbackMode: false,
  returnOnlyMode: false,
  userHasMadeSelections: false,
  initialSelectionMade: false,
  processResults: null,
  orNumber: "",
};

const INITIAL_RESULTS = {
  payment: { successful: [], failed: [] },
  returns: { successful: [], failed: [], skipped: [] },
  verification: { successful: [], failed: [] },
  summary: { paidFines: 0, returnedLoans: 0, errors: 0 },
};

export const PaymentModal = ({
  isOpen,
  selectedPatron,
  overrideData,
  calculatedTotal,
  isUsingOverrides,
  selectedBooks,
  paymentImagePreview,
  paymentMessage,
  isPaymentLoading,
  onClose,
  onImageUpload,
  onPayFine,
  getBookFineAmount,
  onLoanStatusUpdate,
}) => {
  const [state, setState] = useState(INITIAL_STATE);
  const lastPatronEmailRef = useRef();

  const getEffectiveFineAmount = (fine) => {
    if (isUsingOverrides && overrideData?.useManualTotal) {
      return 0;
    }
    return getFineAmount(fine);
  };

  // Reset modal state
  const resetModalState = () => {
    setState(INITIAL_STATE);
    lastPatronEmailRef.current = undefined;
  };

  // Close modal and reset
  const resetStateAndClose = () => {
    resetModalState();
    onClose();
  };

  // Extract fine data helpers
  const getFineAmount = (fine) =>
    parseFloat(fine.totalFine || fine.total_fine || 0);
  const getFineId = (fine) => fine.loanId || fine.loan_id;
  const getFineTitle = (fine) =>
    fine.itemTitle || fine.item_title || fine.description || "Unknown Item";

  // Create fallback fines from selected books
  const createFallbackFines = () => {
    if (!selectedBooks?.length) {
      setState((prev) => ({ ...prev, patronFines: [], selectedFines: [] }));
      return;
    }

    const bookBasedFines = selectedBooks.map((book, index) => ({
      id: book.loanId || book.metadata?.pid || `book_${index}`,
      itemTitle: book.title || book.itemTitle || book.metadata?.document?.title,
      totalFine: getBookFineAmount ? getBookFineAmount(book) : 0,
      status: "active",
      statusDisplay: "Overdue",
      dateDisplay: book.dueDate || book.metadata?.end_date || "N/A",
      loanId: book.loanId || book.metadata?.pid,
      itemBarcode: book.itemBarcode || book.metadata?.item?.barcode,
      description: `Fine for ${
        book.title || book.itemTitle || book.metadata?.document?.title
      }`,
    }));

    setState((prev) => ({
      ...prev,
      patronFines: bookBasedFines,
      selectedFines: prev.userHasMadeSelections
        ? prev.selectedFines
        : bookBasedFines.map((fine) => fine.id),
      initialSelectionMade:
        prev.initialSelectionMade || !prev.userHasMadeSelections,
    }));
  };

  // Auto-select matching fines
  const autoSelectMatchingFines = (activeFines) => {
    if (!selectedBooks?.length) {
      setState((prev) => ({ ...prev, selectedFines: [] }));
      return;
    }

    const validBooks = selectedBooks.filter(
      (book) => book && typeof book === "object"
    );
    if (!validBooks.length) {
      setState((prev) => ({ ...prev, selectedFines: [] }));
      return;
    }

    const matchingFines = activeFines.filter((fine) => {
      return validBooks.some((book) => {
        const loanIdMatch =
          book.loanId && fine.loan_id && book.loanId === fine.loan_id;
        const barcodeMatch =
          book.itemBarcode &&
          fine.item_barcode &&
          book.itemBarcode === fine.item_barcode;
        const titleMatch =
          book.title && fine.item_title && book.title === fine.item_title;
        const metadataLoanMatch =
          book.metadata?.pid &&
          fine.loan_id &&
          book.metadata.pid === fine.loan_id;
        return loanIdMatch || barcodeMatch || titleMatch || metadataLoanMatch;
      });
    });

    setState((prev) => ({
      ...prev,
      selectedFines: matchingFines.map((fine) => fine.id),
    }));
  };

  // Check for unreturned loans
  const checkForUnreturnedLoans = async (paidFines) => {
    const loansNeedingReturn = [];

    for (const fine of paidFines) {
      const loanId = getFineId(fine);
      if (!loanId) continue;

      const loanResponse = await loanApi.get(loanId);
      const loan = loanResponse.data;
      const loanStatus = loan.metadata?.state || loan.state || loan.status;

      if (RETURNABLE_STATES.includes(loanStatus)) {
        loansNeedingReturn.push({
          ...fine,
          needsReturn: true,
          currentLoanStatus: loanStatus,
          statusDisplay: `Paid fine - Loan needs return (${loanStatus})`,
        });
      }
    }

    return loansNeedingReturn;
  };

  // Fetch patron fines with fallback
  const fetchPatronFinesWithFallback = async () => {
    if (!isOpen || !selectedPatron?.email) return;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      usingFallbackMode: false,
      returnOnlyMode: false,
      processResults: null,
    }));

    try {
      const response = await fineApi.getUserFines(selectedPatron.email);

      if (response.data?.hits?.length > 0) {
        const allFines = response.data.hits;
        const activeFines = allFines.filter((fine) =>
          ACTIVE_FINE_STATUSES.includes(fine.status)
        );
        const paidFines = allFines.filter((fine) => fine.status === "paid");

        if (activeFines.length > 0) {
          let processedFines = activeFines;

          if (overrideData?.overridesByLoanId) {
            processedFines = activeFines.map((fine) => {
              const loanId = fine.loan_id || fine.loanId || fine.id;

              if (
                loanId &&
                overrideData.overridesByLoanId[loanId] !== undefined
              ) {
                const overrideAmount = parseFloat(
                  overrideData.overridesByLoanId[loanId]
                );

                return {
                  ...fine,
                  totalFine: overrideAmount,
                  total_fine: overrideAmount,
                  originalFine: fine.totalFine || fine.total_fine,
                  isOverridden: true,
                };
              }

              return fine;
            });
          }

          setState((prev) => ({
            ...prev,
            patronFines: processedFines,
            usingFallbackMode: false,
          }));

          if (!state.userHasMadeSelections && !state.initialSelectionMade) {
            autoSelectMatchingFines(activeFines);
            setState((prev) => ({ ...prev, initialSelectionMade: true }));
          }
          return;
        } else if (paidFines.length > 0) {
          const loansNeedingReturn = await checkForUnreturnedLoans(paidFines);
          if (loansNeedingReturn.length > 0) {
            setState((prev) => ({
              ...prev,
              returnOnlyMode: true,
              patronFines: loansNeedingReturn,
              selectedFines:
                prev.userHasMadeSelections || prev.initialSelectionMade
                  ? prev.selectedFines
                  : loansNeedingReturn.map((fine) => fine.id),
              initialSelectionMade:
                prev.initialSelectionMade || !prev.userHasMadeSelections,
            }));
            return;
          }
        }
      }

      if (selectedBooks?.length > 0) {
        setState((prev) => ({ ...prev, usingFallbackMode: true }));
        createFallbackFines();
      } else {
        setState((prev) => ({
          ...prev,
          patronFines: [],
          selectedFines: [],
          usingFallbackMode: false,
        }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: "Failed to load patron fines. Please try again.",
        patronFines: [],
        selectedFines: [],
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  // Return loan after payment
  const returnLoanAfterPayment = async (loanId, paymentInfo) => {
    try {
      const loanResponse = await loanApi.get(loanId);
      const loan = loanResponse.data;
      const currentStatus = loan.metadata?.state || loan.state || loan.status;

      if (!RETURNABLE_STATES.includes(currentStatus)) {
        return {
          success: false,
          loanId,
          error: `Loan is already in ${currentStatus} state`,
          currentStatus,
          skipped: true,
          timestamp: new Date().toISOString(),
        };
      }

      const returnData = {
        transaction_date: new Date().toISOString(),
        notes: `Auto-return after fine ${paymentInfo.fineId} payment. Ref: ${paymentInfo.paymentReference}`,
        source: "unified_payment_return",
        patron_email: paymentInfo.patronEmail,
        force_return: true,
      };

      const availableActions = loan.availableActions || {};
      let returnSuccess = false;
      let returnResponse = null;

      // Try different return methods
      const returnMethods = [
        async () => {
          if (availableActions.checkin) {
            return await loanApi.doAction(
              `/circulation/loans/${loanId}/checkin`,
              loan.metadata?.document_pid || loan.document_pid,
              loan.metadata?.patron_pid || loan.patron_pid,
              {
                ...returnData,
                itemPid: loan.metadata?.item_pid || loan.item_pid,
              }
            );
          }
          return null;
        },
        async () => {
          if (availableActions["checkout-return"]) {
            return await loanApi.doAction(
              loanId,
              "checkout-return",
              returnData
            );
          }
          return null;
        },
        async () => {
          if (availableActions.checkin) {
            const response = await fetch(availableActions.checkin, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(returnData),
            });
            return response.ok
              ? { status: response.status, data: await response.json() }
              : null;
          }
          return null;
        },
      ];

      for (const method of returnMethods) {
        const result = await method();
        if (result && (result.status === 200 || result.status === 202)) {
          returnSuccess = true;
          returnResponse = result;
          break;
        }
      }

      if (returnSuccess) {
        return {
          success: true,
          loanId,
          previousStatus: currentStatus,
          newStatus: "ITEM_RETURNED",
          response: returnResponse?.data,
          timestamp: new Date().toISOString(),
        };
      } else {
        throw new Error("All return methods failed");
      }
    } catch (error) {
      return {
        success: false,
        loanId,
        error: error.message || "Return operation failed",
        timestamp: new Date().toISOString(),
      };
    }
  };

  // Verify loan return
  const verifyLoanReturn = async (loanId) => {
    for (let attempt = 1; attempt <= VERIFICATION_ATTEMPTS; attempt++) {
      if (attempt > 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }

      const response = await loanApi.get(loanId, {
        params: { _t: Date.now(), force_refresh: true },
      });

      const loan = response.data;
      const currentStatus = loan.metadata?.state || loan.state || loan.status;

      if (currentStatus === "ITEM_RETURNED") {
        return { success: true, newStatus: currentStatus, attempts: attempt };
      }

      if (attempt === VERIFICATION_ATTEMPTS) {
        return { success: false, currentStatus, attempts: attempt };
      }
    }

    return { success: false, attempts: VERIFICATION_ATTEMPTS };
  };

  // Handle unified payment and return
  const handleUnifiedPaymentAndReturn = async () => {
    if (state.selectedFines.length === 0) {
      setState((prev) => ({
        ...prev,
        error: "Please select at least one fine to process",
      }));
      return;
    }

    if (state.returnOnlyMode) {
      return await handleReturnOnlySubmit();
    }

    setState((prev) => ({
      ...prev,
      paymentProcessing: true,
      error: "",
      processResults: null,
    }));

    try {
      const results = { ...INITIAL_RESULTS };

      for (const fineId of state.selectedFines) {
        try {
          const fine = state.patronFines.find((f) => f.id === fineId);
          if (!fine) {
            throw new Error(`Cannot find fine details for ${fineId}`);
          }

          const loanId = getFineId(fine);
          if (!loanId) {
            throw new Error(`No loan ID found for fine ${fineId}`);
          }

          // Pay the fine with OR number or image URL
          const paymentData = {
            payment_reference: `PAYMENT_${Date.now()}_${fineId}`,
            or_number: state.orNumber?.trim() || null,
            image_url: paymentImagePreview || null,
            notes: `Payment for fine ${fineId} via web interface${
              state.orNumber ? ` - OR# ${state.orNumber}` : ""
            }`,
            patron_email: selectedPatron.email,
            patron_name: selectedPatron.name,
            amount: getFineAmount(fine),
          };

          const paymentResult = await fineApi.pay(fineId, paymentData);

          if (paymentResult.status === 200 || paymentResult.status === 201) {
            results.payment.successful.push({
              fineId,
              loanId,
              amount: paymentData.amount,
              orNumber: state.orNumber,
              imageUrl: paymentImagePreview,
              result: paymentResult.data,
              timestamp: new Date().toISOString(),
            });
            results.summary.paidFines++;

            // Audit logging after successful payment
            try {
              const userResponse = await userApi.get();
              const currentUser = userResponse?.data || {
                username: "unknown",
                full_name: "Unknown User",
              };

              await AuditService.api.createLog({
                action: "Fine Payment",
                details: `Paid fine ${fineId} (₱${paymentData.amount.toFixed(
                  2
                )}) for loan ${loanId} by ${currentUser.full_name} (${
                  currentUser.username
                })${state.orNumber ? ` - OR# ${state.orNumber}` : ""}.`,
                created_by: currentUser.full_name,
              });
            } catch (auditError) {
              console.error(
                `Audit logging failed for fine ${fineId}:`,
                auditError
              );
            }

            // Return the loan
            const returnResult = await returnLoanAfterPayment(loanId, {
              fineId,
              paymentReference: paymentData.payment_reference,
              patronEmail: selectedPatron.email,
              patronName: selectedPatron.name,
            });

            returnResult.fineId = fineId;

            if (returnResult.success) {
              results.returns.successful.push(returnResult);
              results.summary.returnedLoans++;

              // Verify the return
              const verificationResult = await verifyLoanReturn(loanId);
              if (verificationResult.success) {
                results.verification.successful.push({
                  fineId,
                  loanId,
                  verifiedStatus: verificationResult.newStatus,
                  timestamp: new Date().toISOString(),
                });
              }
            } else {
              results.returns.failed.push(returnResult);
              results.summary.errors++;
            }
          } else {
            throw new Error(
              `Payment failed with status: ${paymentResult.status}`
            );
          }
        } catch (error) {
          results.payment.failed.push({
            fineId,
            error: error.message,
            timestamp: new Date().toISOString(),
          });
          results.summary.errors++;
        }
      }

      setState((prev) => ({ ...prev, processResults: results }));

      if (onPayFine) await onPayFine();

      if (results.returns.successful.length > 0 && onLoanStatusUpdate) {
        const returnedLoanIds = results.returns.successful
          .filter((result) => state.selectedFines.includes(result.fineId))
          .map((result) => result.loanId);

        if (returnedLoanIds.length > 0) {
          onLoanStatusUpdate(returnedLoanIds);
        }
      }

      if (results.summary.errors === 0) {
        setTimeout(() => resetStateAndClose(), AUTO_CLOSE_DELAY);
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: `Process failed: ${
          err.message || "Please try again or contact support."
        }`,
      }));
    } finally {
      setState((prev) => ({ ...prev, paymentProcessing: false }));
    }
  };

  // Handle return-only mode
  const handleReturnOnlySubmit = async () => {
    if (state.selectedFines.length === 0) {
      setState((prev) => ({ ...prev, error: "Please select items to return" }));
      return;
    }

    setState((prev) => ({
      ...prev,
      paymentProcessing: true,
      error: "",
      processResults: null,
    }));

    try {
      const loanIdsToReturn = state.patronFines
        .filter((fine) => state.selectedFines.includes(fine.id))
        .map((fine) => getFineId(fine))
        .filter(Boolean);

      const results = {
        returns: { successful: [], failed: [], skipped: [] },
        summary: { returnedLoans: 0, errors: 0 },
      };

      for (const loanId of loanIdsToReturn) {
        try {
          const returnResult = await returnLoanAfterPayment(loanId, {
            patronEmail: selectedPatron.email,
            patronName: selectedPatron.name,
            notes: "Items returned after fine payment (return-only mode)",
          });

          if (returnResult.success) {
            results.returns.successful.push(returnResult);
            results.summary.returnedLoans++;
          } else {
            results.returns.failed.push(returnResult);
            results.summary.errors++;
          }
        } catch (error) {
          results.returns.failed.push({
            loanId,
            error: error.message,
            timestamp: new Date().toISOString(),
          });
          results.summary.errors++;
        }
      }

      setState((prev) => ({ ...prev, processResults: results }));

      if (results.returns.successful.length > 0 && onLoanStatusUpdate) {
        const returnedLoanIds = results.returns.successful.map(
          (result) => result.loanId
        );
        onLoanStatusUpdate(returnedLoanIds);
      }

      setTimeout(() => resetStateAndClose(), AUTO_CLOSE_DELAY);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: `Return failed: ${
          err.message || "Please try again or contact support."
        }`,
      }));
    } finally {
      setState((prev) => ({ ...prev, paymentProcessing: false }));
    }
  };

  // Handle fine selection
  const handleFineSelection = (fineId, isSelected) => {
    setState((prev) => ({
      ...prev,
      userHasMadeSelections: true,
      selectedFines: isSelected
        ? [...prev.selectedFines, fineId]
        : prev.selectedFines.filter((id) => id !== fineId),
    }));
  };

  // Handle select/deselect all
  const handleSelectAllFines = () => {
    setState((prev) => ({
      ...prev,
      userHasMadeSelections: true,
      selectedFines: prev.patronFines
        .filter((fine) => fine.status === "active" || prev.returnOnlyMode)
        .map((fine) => fine.id),
    }));
  };

  const handleDeselectAllFines = () => {
    setState((prev) => ({
      ...prev,
      userHasMadeSelections: true,
      selectedFines: [],
    }));
  };

  // Handle OR number input change
  const handleOrNumberChange = (e) => {
    const value = e.target.value;
    setState((prev) => ({
      ...prev,
      orNumber: value,
    }));
  };

  const calculateTotalFromFines = () => {
    // If using manual total override, return that
    if (isUsingOverrides && overrideData?.useManualTotal) {
      return parseFloat(overrideData.manualTotalOverride) || 0;
    }

    if (!state.patronFines?.length || !state.selectedFines?.length) {
      return 0;
    }

    const selectedFineObjects = state.patronFines.filter((fine) =>
      state.selectedFines.includes(fine.id)
    );

    return selectedFineObjects.reduce((total, fine, index) => {
      const amount = getEffectiveFineAmount(fine, index);
      return total + amount;
    }, 0);
  };

  // Effects
  useEffect(() => {
    if (
      isOpen &&
      selectedPatron?.email &&
      lastPatronEmailRef.current !== selectedPatron.email
    ) {
      lastPatronEmailRef.current = selectedPatron.email;
      resetModalState();
      fetchPatronFinesWithFallback();
    } else if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, selectedPatron?.email]);

  useEffect(() => {
    if (
      isOpen &&
      selectedPatron?.email &&
      state.initialSelectionMade &&
      !state.loading &&
      state.usingFallbackMode &&
      selectedBooks?.length > 0
    ) {
      createFallbackFines();
    }
  }, [selectedBooks]);

  // Derived values
  const shouldShowContent = isOpen && selectedPatron?.email && !state.loading;
  const hasPatronFines = state.patronFines?.length > 0;
  const totalAmount = calculateTotalFromFines();
  const isProcessing = state.paymentProcessing || isPaymentLoading;

  if (!selectedPatron || !isOpen) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={onClose} size="large">
      {/* Processing Overlay */}
      {isProcessing && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.7)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Icon name="spinner" size="huge" loading color="blue" />
            <Header as="h3" color="blue" style={{ marginTop: 16 }}>
              Processing Payment...
            </Header>
            <p>Please wait while your payment is being processed.</p>
          </div>
        </div>
      )}

      <Header
        icon="payment"
        content={
          state.returnOnlyMode
            ? "Return Items (Fines Already Paid)"
            : "Submit Payment & Auto-Return Items"
        }
      />

      <Modal.Content>
        {state.error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{state.error}</p>
          </Message>
        )}

        {shouldShowContent ? (
          <>
            <OverrideNotification overrideData={overrideData} />

            {!state.returnOnlyMode && (
              <PaymentDetails
                selectedPatron={selectedPatron}
                calculatedTotal={totalAmount}
                isUsingOverrides={isUsingOverrides}
              />
            )}

            {isProcessing && (
              <Message info>
                <Icon name="clock" loading />
                {state.returnOnlyMode
                  ? "Processing returns..."
                  : "Processing payment and returns..."}
              </Message>
            )}

            <Grid columns={2} stackable>
              <Grid.Column width={8}>
                <Header size="small">
                  Select Fines to {state.returnOnlyMode ? "Return" : "Pay"}
                </Header>

                {state.loading ? (
                  <Message info>Loading patron fines...</Message>
                ) : !hasPatronFines ? (
                  <Message warning>
                    No active fines found for this patron.
                    {selectedBooks?.length > 0 &&
                      " Please check if the selected books have associated fines."}
                  </Message>
                ) : (
                  <div>
                    <div style={{ marginBottom: "10px" }}>
                      <Button
                        size="mini"
                        onClick={handleSelectAllFines}
                        disabled={isProcessing}
                      >
                        Select All
                      </Button>
                      <Button
                        size="mini"
                        onClick={handleDeselectAllFines}
                        disabled={isProcessing}
                      >
                        Deselect All
                      </Button>
                    </div>

                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {state.patronFines.map((fine, index) => (
                        <div
                          key={fine.id}
                          style={{
                            padding: "12px",
                            border: "1px solid #ddd",
                            marginBottom: "6px",
                            borderRadius: "4px",
                            backgroundColor: state.selectedFines.includes(
                              fine.id
                            )
                              ? state.returnOnlyMode
                                ? "#e6ffe6"
                                : "#e6f3ff"
                              : "white",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <input
                              type="checkbox"
                              checked={state.selectedFines.includes(fine.id)}
                              onChange={(e) =>
                                handleFineSelection(fine.id, e.target.checked)
                              }
                              disabled={isProcessing}
                              style={{ marginRight: "12px" }}
                            />
                            <div style={{ flex: 1 }}>
                              <strong style={{ color: "#2c3e50" }}>
                                {getFineTitle(fine)}
                              </strong>
                              <br />
                              <small style={{ color: "#7f8c8d" }}>
                                {!state.returnOnlyMode && (
                                  <>
                                    Amount: ₱{getFineAmount(fine).toFixed(2)}
                                    {fine.isOverridden && fine.originalFine && (
                                      <span
                                        style={{
                                          color: "#e67e22",
                                          marginLeft: "4px",
                                          fontSize: "0.85em",
                                          fontWeight: "500",
                                        }}
                                      >
                                        (was ₱{fine.originalFine.toFixed(2)})
                                      </span>
                                    )}
                                    {" | "}
                                  </>
                                )}
                                {fine.dateDisplay || fine.start_date || "N/A"}
                              </small>
                              {getFineId(fine) && (
                                <>
                                  <br />
                                  <small style={{ color: "#7f8c8d" }}>
                                    Loan ID: {getFineId(fine)}
                                  </small>
                                </>
                              )}
                              {state.returnOnlyMode && fine.needsReturn && (
                                <>
                                  <br />
                                  <small
                                    style={{
                                      color: "#e67e22",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon name="warning sign" size="small" />
                                    Fine already paid - Item needs to be
                                    returned
                                  </small>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {state.selectedFines.length === 0 && hasPatronFines && (
                      <Message warning>
                        <Message.Header>
                          No {state.returnOnlyMode ? "items" : "fines"} selected
                        </Message.Header>
                        <p>
                          Please select at least one{" "}
                          {state.returnOnlyMode
                            ? "item to return"
                            : "fine to pay"}
                          .
                        </p>
                      </Message>
                    )}
                  </div>
                )}
              </Grid.Column>

              <Grid.Column width={8}>
                {!state.returnOnlyMode && (
                  <>
                    {/* OR Number Input Field - Only show if amount > 0 */}
                    {totalAmount > 0 && (
                      <Form>
                        <Form.Field>
                          <label
                            style={{
                              fontSize: "1.07142857rem",
                              marginBottom: "15px",
                            }}
                          >
                            Official Receipt (OR) Number{" "}
                            <span
                              style={{ color: "#999", fontWeight: "normal" }}
                            >
                              (Optional)
                            </span>
                          </label>
                          <Input
                            value={state.orNumber}
                            onChange={handleOrNumberChange}
                            disabled={isProcessing}
                            icon="hashtag"
                            iconPosition="left"
                          />
                          <div
                            style={{
                              marginBottom: "21px",
                              fontSize: "0.8em",
                              color: "#666",
                            }}
                          >
                            Enter the Official Receipt number for record-keeping
                            purposes.
                          </div>
                        </Form.Field>
                      </Form>
                    )}

                    {/* File Upload Section - Only show if amount > 0 */}
                    {totalAmount > 0 && (
                      <FileUploadSection
                        paymentImagePreview={paymentImagePreview}
                        paymentMessage={paymentMessage}
                        onImageUpload={onImageUpload}
                      />
                    )}

                    {/* Message when amount is 0 */}
                    {totalAmount === 0 && (
                      <Message info>
                        <Message.Header>
                          <Icon name="info circle" />
                          No Payment Required
                        </Message.Header>
                        <p>
                          The total amount is ₱0.00. Payment receipt and OR
                          number are not required.
                        </p>
                      </Message>
                    )}
                  </>
                )}

                {state.returnOnlyMode && (
                  <Segment>
                    <Header size="small">
                      <Icon name="info circle" color="blue" />
                      Return Information
                    </Header>
                    <Message info size="small">
                      <List bulleted>
                        <List.Item>
                          All fines for this patron have been paid
                        </List.Item>
                        <List.Item>
                          Items can now be returned without additional payment
                        </List.Item>
                        <List.Item>
                          Loan status will be updated automatically
                        </List.Item>
                        <List.Item>No payment receipt required</List.Item>
                      </List>
                    </Message>
                  </Segment>
                )}
              </Grid.Column>
            </Grid>
          </>
        ) : state.loading ? (
          <Message info>
            <Icon name="clock" loading />
            Loading patron information...
          </Message>
        ) : !selectedPatron?.email ? (
          <Message warning>
            <Message.Header>No Patron Selected</Message.Header>
            <p>Please select a patron to view their fines.</p>
          </Message>
        ) : null}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>

        {shouldShowContent && hasPatronFines && (
          <Button
            color={state.returnOnlyMode ? "blue" : "green"}
            onClick={handleUnifiedPaymentAndReturn}
            loading={isProcessing}
            disabled={isProcessing || state.selectedFines.length === 0}
          >
            {state.returnOnlyMode ? (
              <>
                <Icon name="book" />
                Return Items Only ({state.selectedFines.length})
              </>
            ) : (
              <>
                <Icon name="payment" />
                Pay Fines & Return Items ({state.selectedFines.length}) (₱
                {totalAmount.toFixed(2)})
              </>
            )}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

PaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedPatron: PropTypes.object,
  overrideData: PropTypes.object,
  calculatedTotal: PropTypes.number.isRequired,
  isUsingOverrides: PropTypes.bool.isRequired,
  selectedBooks: PropTypes.array.isRequired,
  paymentImagePreview: PropTypes.string,
  paymentMessage: PropTypes.string,
  isPaymentLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onPayFine: PropTypes.func.isRequired,
  getBookFineAmount: PropTypes.func.isRequired,
  onLoanStatusUpdate: PropTypes.func,
};

PaymentModal.defaultProps = {
  selectedPatron: null,
  overrideData: null,
  paymentImagePreview: null,
  paymentMessage: "",
  onLoanStatusUpdate: null,
};

export default PaymentModal;
